import React, { useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { ChangeEvent, useContext } from 'react';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import PasswordTextField from 'shared/lib/components/controls/textFields/passwordTextField';
import ButtonsContainer from 'shared/lib/components/controls/buttons/buttonsContainer';
import { profileApi } from 'entities/profile/api/profileApi';
import { AxiosError } from 'axios';
import { useBooleanScreens } from 'shared/hooks/useBooleanScreens';
import ActionInlineButton from 'shared/lib/components/controls/buttons/actionInlineButton';
import IconCross from 'shared/icons/cross';
import IconOk from 'shared/icons/iconOk';

const PASSWORD_MAX_LENGTH = 100;

export default observer(function PasswordEditForm({
  handleGoToProfileEdit,
}: {
  handleGoToProfileEdit: () => void;
}) {
  const { isLessLaptop } = useBooleanScreens();
  const { t } = useTranslation();
  const {
    mobxStore: { authStore, notificationsStore, stateStore },
  } = useContext(MobxContext);
  const {
    spacings: {
      sp,
      profilePage: { profileEdit },
    },
  } = useContext(UiContext);

  const validationSchema = yup.object({
    passwordOld: yup.string().required(t('errors.validationSchema.required')),
    passwordNew: yup
      .string()
      .required(t('errors.validationSchema.required'))
      .min(8, t('errors.validationSchema.passwordTooShort'))
      .max(
        PASSWORD_MAX_LENGTH,
        t('errors.validationSchema.passwordTooLong', {
          maxLength: PASSWORD_MAX_LENGTH,
        })
      ),
    passwordConfirm: yup
      .mixed()
      .required(t('errors.validationSchema.required'))
      .oneOf(
        [yup.ref('passwordNew')],
        t('errors.validationSchema.passwordNotConfirmed')
      ),
  });

  const [isError, setIsError] = useState(false);

  const [formikSubmited, setFormikSubmited] = useState(false);

  const formik = useFormik({
    initialValues: {
      passwordOld: '',
      passwordNew: '',
      passwordConfirm: '',
    },
    validateOnBlur: false,
    validateOnChange: formikSubmited,
    validationSchema: validationSchema,
    onSubmit: async (passwordData) => {
      stateStore.isSomeLoading = true;

      try {
        await profileApi.resetPassword(
          passwordData.passwordOld,
          passwordData.passwordNew
        );
        authStore.logout();
      } catch (e) {
        const err = e as AxiosError;
        console.error(err);

        if (err.response?.status === 401) {
          setIsError(true);
        } else {
          notificationsStore.errorMessage = t('notifications.errors.default');
        }
      }

      stateStore.isSomeLoading = false;
    },
  });

  const isFormikFieldError = (field: keyof typeof formik.initialValues) =>
    formik.touched[field] && !!formik.errors[field];

  const getFormikFieldErrorText = (field: keyof typeof formik.initialValues) =>
    formik.touched[field] && formik.errors[field];

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    authStore.clearError();
    formik.handleChange(e);
  };

  return (
    <Box
      component="form"
      sx={{
        width: isLessLaptop ? 1 : profileEdit.formWidth,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: sp.s20,
      }}
      onSubmit={formik.handleSubmit}
    >
      <PasswordTextField
        placeholder={t('profile.enterPasswordOld')}
        id="passwordOld"
        name="passwordOld"
        value={formik.values.passwordOld}
        onChange={handleOnChange}
        onBlur={formik.handleBlur}
        error={isFormikFieldError('passwordOld') || isError}
        helperText={
          getFormikFieldErrorText('passwordOld') ||
          (isError && t('errors.wrongCurrentPassword'))
        }
      ></PasswordTextField>

      <PasswordTextField
        placeholder={t('profile.enterPasswordNew')}
        id="passwordNew"
        name="passwordNew"
        value={formik.values.passwordNew}
        onChange={handleOnChange}
        onBlur={formik.handleBlur}
        error={isFormikFieldError('passwordNew') || isError}
        helperText={getFormikFieldErrorText('passwordNew')}
      ></PasswordTextField>

      <PasswordTextField
        placeholder={t('profile.enterPasswordConfirm')}
        id="passwordConfirm"
        name="passwordConfirm"
        value={formik.values.passwordConfirm}
        onChange={handleOnChange}
        onBlur={formik.handleBlur}
        error={isFormikFieldError('passwordConfirm') || isError}
        helperText={getFormikFieldErrorText('passwordConfirm')}
      ></PasswordTextField>

      <ButtonsContainer variant="full">
        <ActionInlineButton
          sizeVariant="small"
          Icon={IconCross}
          onClick={handleGoToProfileEdit}
        >
          {t('common.cancel')}
        </ActionInlineButton>

        <ActionInlineButton
          sx={{ justifyContent: 'end' }}
          sizeVariant="small"
          Icon={IconOk}
          disabled={!formik.isValid || !formik.dirty}
          onClick={() => {
            formik.submitForm();
            setFormikSubmited(true);
          }}
        >
          {t('common.save')}
        </ActionInlineButton>
      </ButtonsContainer>
    </Box>
  );
});
